// Sprint 8 — Perfil público
import { QRCodeSVG } from "qrcode.react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { SocialLinkIcon } from "@/components/profile/social-link-icon";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { PROFILE_THEME_META, type PublicProfileThemeId } from "@/lib/public-profile-theme-vars";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";

type SocialRow = { id: string; label: string; url: string; order: number };

type Props = {
  slug: string;
  origin: string;
  bio: string;
  onBioChange: (v: string) => void;
  profileTheme: PublicProfileThemeId;
  onProfileThemeChange: (v: PublicProfileThemeId) => void;
  primaryColor: string;
  onPrimaryColorChange: (v: string) => void;
};

export function SettingsPublicProfileBlock({
  slug,
  origin,
  bio,
  onBioChange,
  profileTheme,
  onProfileThemeChange,
  primaryColor,
  onPrimaryColorChange,
}: Props) {
  const { refresh } = useAuth();
  const toast = useToast();
  const [links, setLinks] = useState<SocialRow[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const profileUrl = `${origin}/p/${slug}`;

  const reloadLinks = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/dashboard/social-links", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const d = (await r.json()) as { links?: SocialRow[] };
    setLinks(d.links ?? []);
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLinksLoading(true);
      const r = await fetchWithSessionSync("/api/dashboard/social-links", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (r.ok) {
        const d = (await r.json()) as { links?: SocialRow[] };
        setLinks(d.links ?? []);
      }
      setLinksLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  async function downloadProfileQrPng() {
    const svg = document.querySelector<SVGSVGElement>(".profile-public-qr-zone svg");
    if (!svg) return;
    const ser = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([ser], { type: "image/svg+xml;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Réplica exacta del QR mostrado en pantalla: mismo cuadrado y mismo margen blanco
      // que el box (bg:white, p:"3" = 12px) que lo envuelve ahí. Antes esto se estiraba a
      // un canvas 512x580 (no cuadrado), deformando el código respecto al que se ve.
      const scale = 3;
      const qrSize = img.naturalWidth * scale;
      const pad = 12 * scale;
      const size = qrSize + pad * 2;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, pad, pad, qrSize, qrSize);
      URL.revokeObjectURL(u);
      canvas.toBlob((b) => {
        if (!b) {
          toast.error("No se pudo generar la imagen.");
          return;
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `qr-perfil-${slug}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        toast.success("Descarga lista");
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(u);
      toast.error("No se pudo exportar el PNG.");
    };
    img.src = u;
  }

  async function addLink() {
    if (!newLabel.trim() || !newUrl.trim()) {
      toast.error("Completá etiqueta y URL.");
      return;
    }
    setAdding(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/social-links",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: newLabel.trim(), url: newUrl.trim() }),
      },
      refresh,
    );
    setAdding(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(j.error ?? "No se pudo agregar el enlace.");
      return;
    }
    setNewLabel("");
    setNewUrl("");
    await reloadLinks();
    toast.success("Enlace agregado");
  }

  async function removeLink(id: string) {
    const r = await fetchWithSessionSync(
      `/api/dashboard/social-links/${encodeURIComponent(id)}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo eliminar.");
      return;
    }
    const d = (await r.json()) as { links?: SocialRow[] };
    setLinks(d.links ?? []);
    toast.success("Eliminado");
  }

  async function moveLink(id: string, direction: "up" | "down") {
    const r = await fetchWithSessionSync(
      `/api/dashboard/social-links/${encodeURIComponent(id)}/move`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo reordenar.");
      return;
    }
    const d = (await r.json()) as { links?: SocialRow[] };
    setLinks(d.links ?? []);
  }

  const hex = primaryColor?.trim().match(/^#([0-9A-Fa-f]{6})$/) ? primaryColor.trim() : "#4f46e5";

  return (
    <div className={cx(cf.dashSurface, css({ p: "5" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Perfil público</p>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "4", lineHeight: "1.5" })}>
        Tu vitrina en <span className={css({ fontFamily: "mono" })}>/p/{slug}</span>. La biografía usa markdown simple (**negrita**,
        *cursiva*). Los enlaces aparecen con ícono según el sitio. Para reservas sigue usando la página de turnos.
      </p>

      <div className={css({ mb: "6" })}>
        <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "1", fontSize: "md" })}>Tema de colores</p>
        <p className={css({ fontSize: "xs", color: "#64748b", mb: "4", lineHeight: "1.5" })}>
          Personalizá los colores de tu perfil público y de la página de reservas.
        </p>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)" },
            gap: "3",
          })}
        >
          {PROFILE_THEME_META.map((m) => {
            const selected = profileTheme === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onProfileThemeChange(m.id)}
                className={css({
                  textAlign: "left",
                  p: "4",
                  rounded: "xl",
                  borderWidth: "2px",
                  borderStyle: "solid",
                  borderColor: selected ? hex : "#e2e8f0",
                  bg: m.cardTint,
                  cursor: "pointer",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  _hover: { borderColor: selected ? hex : "#cbd5e1" },
                })}
                style={
                  (selected ? { boxShadow: `0 0 0 2px ${hex}` } : undefined) as CSSProperties | undefined
                }
              >
                <div className={css({ display: "flex", gap: "2", mb: "3" })}>
                  {m.swatches.map((c) => (
                    <span
                      key={c}
                      className={css({ w: "9", h: "9", rounded: "md", flexShrink: 0 })}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <p className={css({ fontWeight: "bold", fontSize: "sm", color: "#0f172a", mb: "1" })}>{m.label}</p>
                {selected ? (
                  <p
                    className={css({ fontSize: "xs", fontWeight: "semibold", display: "flex", alignItems: "center", gap: "1" })}
                    style={{ color: hex }}
                  >
                    <Check size={14} strokeWidth={2.5} aria-hidden />
                    Seleccionado
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155", mb: "4" })}>
        <span>Color principal (acentos y botones)</span>
        <div className={css({ display: "flex", alignItems: "center", gap: "3", flexWrap: "wrap" })}>
          <input
            type="color"
            value={hex}
            onChange={(e) => onPrimaryColorChange(e.target.value)}
            className={css({ w: "48px", h: "40px", p: "0", border: "none", cursor: "pointer", borderRadius: "md" })}
            aria-label="Color principal"
          />
          <input
            className={cf.input}
            value={primaryColor}
            onChange={(e) => onPrimaryColorChange(e.target.value)}
            placeholder="#4f46e5"
            spellCheck={false}
          />
        </div>
      </label>

      <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155", mb: "4" })}>
        <span>Biografía (markdown básico para /p)</span>
        <textarea
          className={cx(cf.input, css({ minH: "120px", resize: "vertical", fontFamily: "mono", fontSize: "xs" }))}
          value={bio}
          onChange={(e) => onBioChange(e.target.value)}
          placeholder="Contá quién sos… **destacá** lo importante."
        />
      </label>

      <div
        className={cx(
          "profile-public-qr-zone",
          css({
            mt: "4",
            p: "4",
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "#f8fafc",
            display: "flex",
            flexDir: "column",
            alignItems: "center",
            gap: "3",
            maxW: "20rem",
          }),
        )}
      >
        <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", textAlign: "center" })}>
          QR del perfil público
        </p>
        <div className={css({ bg: "white", p: "3", rounded: "lg" })}>
          <QRCodeSVG value={profileUrl} size={180} level="M" includeMargin />
        </div>
        <p className={css({ fontSize: "xs", color: "#64748b", textAlign: "center", wordBreak: "break-all" })}>{profileUrl}</p>
        <button
          type="button"
          onClick={() => void downloadProfileQrPng()}
          className={css({
            px: "4",
            py: "2.5",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            color: "#334155",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            cursor: "pointer",
          })}
        >
          Descargar PNG
        </button>
      </div>

      <div className={css({ mt: "6" })}>
        <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Enlaces (redes y más)</p>
        <p className={css({ fontSize: "xs", color: "#64748b", mb: "3" })}>
          Ordená con las flechas. El ícono se infiere del dominio de la URL.
        </p>
        {linksLoading ? (
          <p className={css({ fontSize: "sm", color: "#64748b" })}>Cargando…</p>
        ) : links.length === 0 ? (
          <p className={css({ fontSize: "sm", color: "#64748b" })}>Todavía no agregaste enlaces.</p>
        ) : (
          <ul className={css({ display: "flex", flexDir: "column", gap: "2", listStyle: "none", p: "0", m: "0", mb: "4" })}>
            {links.map((l, idx) => (
              <li
                key={l.id}
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: "2",
                  flexWrap: "wrap",
                  p: "3",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  bg: "#fafafa",
                })}
              >
                <div className={css({ display: "flex", flexDir: "column", gap: "0.5" })}>
                  <button
                    type="button"
                    aria-label="Subir"
                    disabled={idx === 0}
                    onClick={() => void moveLink(l.id, "up")}
                    className={css({
                      p: "1",
                      rounded: "md",
                      border: "none",
                      bg: "#e2e8f0",
                      cursor: idx === 0 ? "not-allowed" : "pointer",
                      opacity: idx === 0 ? 0.45 : 1,
                      display: "flex",
                    })}
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Bajar"
                    disabled={idx === links.length - 1}
                    onClick={() => void moveLink(l.id, "down")}
                    className={css({
                      p: "1",
                      rounded: "md",
                      border: "none",
                      bg: "#e2e8f0",
                      cursor: idx === links.length - 1 ? "not-allowed" : "pointer",
                      opacity: idx === links.length - 1 ? 0.45 : 1,
                      display: "flex",
                    })}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                <SocialLinkIcon url={l.url} size={20} className={css({ color: "#475569", flexShrink: 0 })} />
                <div className={css({ flex: "1", minW: "0" })}>
                  <p className={css({ fontWeight: "semibold", fontSize: "sm", color: "#0f172a" })}>{l.label}</p>
                  <p className={css({ fontSize: "xs", color: "#64748b", wordBreak: "break-all" })}>{l.url}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void removeLink(l.id)}
                  className={css({
                    fontSize: "xs",
                    color: "#b91c1c",
                    border: "none",
                    bg: "transparent",
                    cursor: "pointer",
                    fontWeight: "semibold",
                  })}
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
          <input className={cf.input} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Etiqueta (ej. Instagram)" />
          <input className={cf.input} value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://…" />
          <button
            type="button"
            disabled={adding}
            onClick={() => void addLink()}
            className={css({
              px: "4",
              py: "2.5",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              border: "none",
              cursor: adding ? "wait" : "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
              opacity: adding ? 0.85 : 1,
            })}
          >
            {adding ? "Agregando…" : "Agregar enlace"}
          </button>
        </div>
      </div>
    </div>
  );
}
